import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
} from "@mui/material";

const TranslationModal = ({
  open,
  onClose,
  loading,
  completed,
  previewUrl,
  onGenerate,
  onPreview,
  onAccept,
  onReject,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = () => {
    console.log("PREVIEW URL:", previewUrl);
    if (!previewUrl) {
      console.error("Translation preview URL is not available");
      return;
    }

    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
  };

  const handlePreviewReject = () => {
    setPreviewOpen(false);
    onReject();
  };

  const handlePreviewAccept = () => {
    setPreviewOpen(false);
    onAccept();
  };

  return (
    <>
      {/* ===================================================== */}
      {/* MAIN TRANSLATION MODAL */}
      {/* ===================================================== */}

      <Dialog
        open={open}
        onClose={!loading ? onClose : undefined}
        fullWidth
        maxWidth="sm"
      >
        {/* ================================================= */}
        {/* STEP 1 - CONFIRM */}
        {/* ================================================= */}

        {!loading && !completed && (
          <>
            <DialogTitle sx={{ textAlign: "center" }}>
              Translate
            </DialogTitle>

            <DialogContent sx={{ textAlign: "center" }}>
              <Typography>
                Translate this document?
              </Typography>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "center",
                gap: 1,
                pb: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={onGenerate}
              >
                TRANSLATE
              </Button>

              <Button
                variant="outlined"
                onClick={onClose}
              >
                CANCEL
              </Button>
            </DialogActions>
          </>
        )}

        {/* ================================================= */}
        {/* STEP 2 - LOADING */}
        {/* ================================================= */}

        {loading && (
          <>
            <DialogTitle>
              Translate Document
            </DialogTitle>

            <DialogContent>
              <Box
                sx={{
                  textAlign: "center",
                  mt: 4,
                  mb: 3,
                }}
              >
                <CircularProgress
                  size={52}
                  thickness={3}
                />

                <Typography
                  variant="h5"
                  sx={{ mt: 4 }}
                >
                  Generating Translation
                </Typography>

                <Typography
                  sx={{
                    mt: 2,
                    color: "text.secondary",
                    fontSize: "20px",
                  }}
                >
                  Your document is being translated.
                  This may take a few minutes.
                </Typography>

                <Box sx={{ mt: 5 }}>
                  <LinearProgress />
                </Box>

                <Typography
                  sx={{
                    mt: 4,
                    color: "text.secondary",
                    fontSize: "20px",
                  }}
                >
                  Please don't close this window.
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "flex-end",
                pb: 3,
                pr: 3,
              }}
            >
              <Button disabled>
                PROCESSING...
              </Button>
            </DialogActions>
          </>
        )}

        {/* ================================================= */}
        {/* STEP 3 - COMPLETED */}
        {/* ================================================= */}

        {completed && (
          <>
            <DialogTitle>
              Translate Document
            </DialogTitle>

            <DialogContent>
              <Box
                sx={{
                  textAlign: "center",
                  mt: 3,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  color="success.main"
                >
                  ✓ Translation Generated Successfully
                </Typography>

                <Typography sx={{ mt: 2 }}>
                  The translation is ready for review.
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    color: "text.secondary",
                  }}
                >
                  Please preview the translated document
                  before accepting.
                </Typography>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "flex-end",
                gap: 1,
                pb: 3,
                pr: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handlePreview}
              >
                PREVIEW
              </Button>

              <Button
                color="error"
                onClick={onReject}
              >
                REJECT
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={onAccept}
              >
                ACCEPT
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ===================================================== */}
      {/* PDF PREVIEW MODAL */}
      {/* ===================================================== */}

      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: "90vh",
          },
        }}
      >
        <DialogTitle>
          Translation Preview
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            backgroundColor: "#f5f5f5",
          }}
        >
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title="Translation Preview"
              width="100%"
              height="100%"
              style={{
                border: "none",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography>
                Preview URL is not available.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePreviewClose}
          >
            CLOSE
          </Button>

          <Button
            color="error"
            onClick={handlePreviewReject}
          >
            REJECT
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handlePreviewAccept}
          >
            ACCEPT
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TranslationModal;